import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { api, apiErr, TEXTURES, waLink, track } from "../lib/api";
import { Section, Reveal, Overline, Button } from "../components/primitives";
import PageHero from "../components/PageHero";
import Seo from "../components/Seo";

// ---------- OUR STORY ----------
export function OurStory() {
  return (
    <div>
      <Seo title="Our Story" description="Cultiva Botanics combines India's botanical heritage with modern product presentation, convenience and quality-focused thinking. Rooted in nature, created for modern living." />
      <PageHero chapter="About" overline="Rooted in Nature. Created for Modern Living." title="Rooted in Nature. Created for Modern Living." />
      <Section className="py-20 grid lg:grid-cols-2 gap-16 items-center">
        <Reveal><img src={TEXTURES.botanical} alt="Botanical ingredients" loading="lazy" className="w-full h-[540px] object-cover rounded-md" /></Reveal>
        <Reveal delay={0.1}>
          <p className="font-serif text-3xl text-botanical leading-snug">Cultiva Botanics was created around a simple idea: natural botanical ingredients should be easier to discover, understand and incorporate into everyday life.</p>
          <p className="text-muted-foreground font-light text-lg mt-8 leading-relaxed">The company combines India's botanical heritage with modern product presentation, convenience and quality-focused thinking — bringing plant-based superfoods to consumers and business partners around the world.</p>
          <div className="mt-8 border-l-2 border-turmeric pl-6">
            <Overline>Long-Term Vision</Overline>
            <p className="font-serif text-2xl text-botanical italic mt-2">Build a trusted global botanical wellness brand originating from India.</p>
          </div>
        </Reveal>
      </Section>
      <Section className="py-20 bg-botanical text-ivory rounded-none text-center grain">
        <div className="relative z-10 max-w-2xl mx-auto">
          <h2 className="display text-5xl md:text-6xl">India-Origin. Global Vision.</h2>
          <div className="mt-8 flex justify-center gap-4 flex-wrap"><Button to="/shop" variant="light">Explore Products</Button><Button to="/partner" variant="outline" className="border-ivory/30 text-ivory hover:bg-ivory hover:text-botanical">Partner With Us</Button></div>
        </div>
      </Section>
    </div>
  );
}

// ---------- BOTANICALS ----------
export function Botanicals() {
  const [products, setProducts] = useState([]);
  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <Seo title="Meet the Botanicals" description="Meet the botanicals behind Cultiva Botanics — Moringa, Hibiscus, Beetroot, Amla, Turmeric and Makhana. Explore each plant-based ingredient." />
      <PageHero chapter="Botanicals" overline="Meet the Botanicals" title="Meet the Botanicals" subtitle="The plant-based ingredients at the heart of everything we make." />
      <Section className="py-16">
        {products.map((p, i) => (
          <Reveal key={p.slug}>
            <Link to={`/products/${p.slug}`} className="group grid lg:grid-cols-2 gap-10 items-center py-14 border-b border-border" data-testid={`botanical-${p.slug}`}>
              <div className={`relative aspect-[16/10] overflow-hidden rounded-md ${i % 2 ? "lg:order-2" : ""}`} style={{ background: `${p.color}0d` }}>
                <img src={p.image} alt={p.name} loading="lazy" className="w-full h-full object-contain p-8 group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className={i % 2 ? "lg:order-1" : ""}>
                <div className="text-[11px] uppercase tracking-widest" style={{ color: p.color }}>{p.positioning}</div>
                <h2 className="display text-5xl md:text-6xl text-botanical mt-3">{p.name}</h2>
                <p className="text-muted-foreground font-light text-lg mt-4 leading-relaxed">{p.ingredient_story}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 font-medium text-botanical">Explore {p.name} <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" /></span>
              </div>
            </Link>
          </Reveal>
        ))}
      </Section>
    </div>
  );
}

// ---------- CONTACT ----------
export function Contact() {
  const [f, setF] = useState({ full_name: "", email: "", message: "", enquiry_type: "contact" });
  const [sending, setSending] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setSending(true);
    try { await api.post("/leads", f); track("contact_form", {}); toast.success("Message sent — we'll be in touch."); setF({ full_name: "", email: "", message: "", enquiry_type: "contact" }); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const inputCls = "w-full bg-transparent border-b border-border focus:border-botanical outline-none py-2.5 text-botanical transition-colors";
  return (
    <div>
      <Seo title="Contact" description="Contact Cultiva Botanics Private Limited. Customer support for products and orders, plus business & export enquiries for distributors, importers, wholesalers and retailers." />
      <PageHero chapter="Contact" overline="Get in Touch" title="Let's start a conversation." />
      <Section className="py-20 grid lg:grid-cols-2 gap-16">
        <div>
          <h3 className="font-serif text-3xl text-botanical">CULTIVA BOTANICS PRIVATE LIMITED</h3>
          <div className="mt-6 space-y-4 text-botanical/80">
            <p className="flex items-start gap-3"><MapPin className="h-5 w-5 text-turmeric shrink-0 mt-0.5" />Lodha Signet I, Unit No. 825, Premier Colony Ground, Manpada, Kalyan, Thane – 421204, Maharashtra, India</p>
            <p className="flex items-center gap-3"><Mail className="h-5 w-5 text-turmeric shrink-0" /><a href="mailto:support@cultivabotanics.com" className="link-underline">support@cultivabotanics.com</a></p>
            <p className="flex items-center gap-3"><Phone className="h-5 w-5 text-turmeric shrink-0" /><a href="tel:+919623299614" className="link-underline">+91 96232 99614</a></p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mt-10">
            <div className="border border-border rounded-md p-6 bg-white">
              <Overline>Customer Support</Overline>
              <p className="text-sm text-muted-foreground mt-2">Questions about products or orders.</p>
              <a href={waLink()} onClick={() => track("whatsapp_click", {})} className="mt-4 inline-flex text-sm font-medium text-botanical link-underline">Chat on WhatsApp</a>
            </div>
            <div className="border border-border rounded-md p-6" style={{ background: "#1A362208" }}>
              <Overline>Business & Export</Overline>
              <p className="text-sm text-muted-foreground mt-2">For distributors, importers, wholesalers and retailers.</p>
              <Link to="/partner" className="mt-4 inline-flex text-sm font-medium text-botanical link-underline">Business Enquiry</Link>
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-8">CIN: U47912MR2026PTC478754</p>
        </div>
        <form onSubmit={submit} className="border border-border rounded-md bg-white p-8" data-testid="contact-form">
          <Overline>Send a Message</Overline>
          <div className="space-y-6 mt-6">
            <input required placeholder="Full Name" className={inputCls} data-testid="contact-name" value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} />
            <input required type="email" placeholder="Email" className={inputCls} data-testid="contact-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
            <textarea required rows={5} placeholder="Your message" className={inputCls} data-testid="contact-message" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} />
            <Button type="submit" testId="contact-submit">{sending ? "Sending…" : "Send Message"}</Button>
          </div>
        </form>
      </Section>
    </div>
  );
}

// ---------- LEGAL ----------
const LEGAL = {
  privacy: { title: "Privacy Policy", body: "This Privacy Policy describes how Cultiva Botanics Private Limited collects, uses and protects information submitted through this website, including business enquiry forms. We use the information you provide solely to respond to your enquiry and manage our business relationship. This is a placeholder policy and will be updated with a full, legally reviewed version." },
  terms: { title: "Terms & Conditions", body: "These Terms & Conditions govern your use of the Cultiva Botanics website. By using this website you agree to these terms. This is a placeholder document and will be updated with a full, legally reviewed version." },
  shipping: { title: "Shipping Policy", body: "International availability, import requirements, customs, duties, taxes and product-specific regulations vary by destination market. Contact our team for market-specific information. This is a placeholder document and will be updated as our shipping operations are finalised." },
  refund: { title: "Refund / Cancellation Policy", body: "This Refund and Cancellation Policy will apply once direct-to-consumer sales are activated. This is a placeholder document and will be updated with a full, legally reviewed version." },
  disclaimer: { title: "Disclaimer", body: "Information on this website is provided for general informational purposes and does not constitute medical or health advice. Product claims are limited to conservative, verified statements. This is a placeholder document and will be updated with a full, legally reviewed version." },
};

export function Legal() {
  const { slug } = useParams();
  const doc = LEGAL[slug] || LEGAL.privacy;
  return (
    <div>
      <Seo title={doc.title} description={`${doc.title} — Cultiva Botanics Private Limited.`} />
      <PageHero chapter="Legal" overline="Legal" title={doc.title} />
      <Section className="py-20 max-w-3xl">
        <p className="text-muted-foreground font-light text-lg leading-relaxed">{doc.body}</p>
        <div className="mt-8 flex gap-6 flex-wrap text-sm">
          {Object.keys(LEGAL).map((k) => <Link key={k} to={`/legal/${k}`} className={`link-underline ${k === slug ? "text-botanical font-medium" : "text-muted-foreground"}`}>{LEGAL[k].title}</Link>)}
        </div>
      </Section>
    </div>
  );
}
